import React from 'react';
import { useInView } from 'react-intersection-observer';
import { useDispatch, useSelector } from 'react-redux';
import { stub as $t } from '@nbcnews/analytics-framework';
import { searchProductsAction } from 'redux/modules/product';

import {
  ArticleContext, RouteContext, VerticalContext, useFeatureFlagContext,
} from 'lib/ContextTypes';
import { verticalSlugMap } from 'lib/vertical';
import { encodeCommerceCurationUrl } from 'lib/buildAffiliateLink';
import { ADDITIONAL_SELLERS } from 'lib/brandFeatures';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { shouldShowProductBacon, shouldShowTodayProductBacon, getRecircBaconTitle } from 'lib/article/taxonomyUtils';

import { Bacon } from 'components/packages/Bacon';
import { useAmpEmbedResize } from 'lib/Hooks/useAmpEmbedResize';

$t('register', 'mbt_bacon_click', { allowDuplicate: true });
$t('register', 'mbt_bacon_scroll');

const defaultProps = {
  configurations: [
    {
      vertical: verticalSlugMap.today,
      /**
      * @param {object} params
      * @param {object} params.article
      * @param {object} params.featureFlags
      */
      condition: ({ article, featureFlags }) => shouldShowTodayProductBacon(article, featureFlags),
      /**
      * @param {object} params
      * @param {object} params.article
      */
      trackingId: ({ article }) => (article.ecommerceEnabled ? 'shoparticlebestsellersbacon' : 'todayarticlebestsellersbacon'),
      bacon: {
        subType: 'oneOneProduct',
        metadata: {
          title: 'Shop TODAY Reader Favorites',
          disclaimerKey: 'DEFAULT',
        },
      },
      query: {
        query: 'type:Product AND taxonomy:today/label/shop-best-sellers',
        size: 20,
        sort: 'datePublished:desc',
      },
    },
    {
      vertical: verticalSlugMap.select,
      /**
      * @param {object} params
      * @param {object} params.article
      */
      condition: ({ article }) => shouldShowProductBacon(article),
      trackingId: 'selectarticlebestsellersbacon',
      bacon: {
        subType: 'oneOneProduct',
        metadata: {
          title: 'Select Bestsellers',
          disclaimerKey: 'DEFAULT',
        },
      },
      query: {
        query: 'type:Product AND taxonomy:nbcnews/section/shopping/most-purchased',
        size: 20,
        sort: 'datePublished:desc',
      },
    },
  ],
  pageRegion: 'article-bottom',
};

/**
 * Transform the array of related products into a format acceptable to the bacon component.
 * @param {object} params
 * @param {object} params.product
 * @param {string} params.pageRoute
 * @param {string} params.vertical
 * @param {string} params.trackingId
 * @returns {object}
 */
const mapItemsForBacon = ({
  product,
  pageRoute,
  vertical,
  trackingId,
}) => ({
  item: ({
    ...product,
    offers: product.offers.map((offer) => ({
      ...offer,
      externalUrl: encodeCommerceCurationUrl({
        vertical,
        baseUrl: offer.externalUrl,
        pageRoute,
        trackingId,
      }),
    })),
  }),
  computedValues: {
    headline: product.name,
    teaseImage: product.teaseImage,
    url: null,
  },
});

/**
 * RecirculationBacon component. Determines its contents from configuration queries or from a provided curated list.
 * @param {object} props
 * @param {Array} props.configurations
 * @param {string} props.pageRegion
 * @param {object} props.curatedList
 */
function RecirculationBacon({
  configurations = defaultProps.configurations,
  pageRegion = defaultProps.pageRegion,
  curatedList = null,
}) {
  useAmpEmbedResize();

  // Using useContext methods instead of imported function for tests
  const article = React.useContext(ArticleContext);
  const vertical = React.useContext(VerticalContext);
  const route = React.useContext(RouteContext);
  const [isScrollTracked, setScrollTracked] = React.useState(false);
  const [isClient, setIsClient] = React.useState(false);
  const isAmp = route.path.indexOf('embedded-pkg') !== -1;
  const featureFlags = useFeatureFlagContext();

  React.useEffect(() => {
    setIsClient(typeof window !== 'undefined');
  }, []);

  // Find a qualifying recirculation configuration if no manual curated list is provided
  const configuration = !curatedList && (
    Boolean(article)
    && Boolean(vertical)
    && Array.isArray(configurations) && configurations.find((c) => vertical === c.vertical
        && typeof c.condition === 'function'
        && c.condition({ article, featureFlags }))
  );

  const [ref, inView] = useInView({
    initialInView: false,
    triggerOnce: true,
    rootMargin: '250px 0px',
  }) || [];

  const dispatch = useDispatch();
  const selector = useSelector((state) => state?.product?.items || []);

  React.useEffect(() => {
    if (!configuration) {
      return;
    }

    const {
      query,
    } = configuration;

    // For AMP we load it immediately since it appears in an iframe.
    if (inView || isAmp) {
      dispatch(searchProductsAction(query));
    }
  }, [inView, isAmp]);

  if (!isClient || (!configuration && !curatedList)) {
    return null;
  }

  const {
    bacon,
    trackingId: tracking,
  } = configuration || {
    bacon: {
      subType: 'oneOneProduct',
      metadata: {
        title: curatedList?.headline?.primary,
        disclaimerKey: 'DEFAULT',
      },
    },
    trackingId: curatedList.ecommerceMetadata?.trackingId,
  };

  const items = curatedList?.content?.items || selector;
  const shouldShowAdditionalSeller = getFeatureConfigForBrand(ADDITIONAL_SELLERS, vertical);
  const trackingId = (typeof tracking === 'function' ? tracking({ article }) : tracking);
  const showStripeBackground = Boolean(configuration
    || (curatedList && !shouldShowProductBacon(article, featureFlags)));

  return (
    <div
      data-testid="recirc-bacon"
      ref={ref}
    >
      {items && items.length > 0 && (
        <div
          className="layout-grid-container mv8 dn-print"
          data-testid="recirc-bacon-wrapper"
        >
          <div className="layout-grid-item">
            <Bacon
              content={{
                ...bacon,
                metadata: {
                  ...bacon.metadata,
                  trackingId,
                  title: getRecircBaconTitle(featureFlags, curatedList, bacon.metadata.title),
                },
                items: items.map((product) => {
                  const pageRoute = window.location.pathname.split('/').filter((i) => i);
                  // Remove the last path since we are inside an article path
                  pageRoute.pop();
                  return mapItemsForBacon({
                    product,
                    pageRoute: pageRoute.join('/'),
                    vertical,
                    trackingId,
                  });
                }),
              }}
              onScroll={(_, { x, width }) => {
                if (!isScrollTracked && (x / width) > 0.5) {
                  $t('track', 'mbt_bacon_scroll', {
                    action: 'scroll',
                  });
                  setScrollTracked(true);
                }
              }}
              pageRegion={pageRegion}
              vertical={vertical}
              shouldShowAdditionalSeller={shouldShowAdditionalSeller}
              trackLink={(item) => {
                $t('track', 'mbt_bacon_click', {
                  action: 'itemClick',
                  id: item.id,
                  name: item.name,
                  seller: item.offers[0]?.seller?.name,
                });
              }}
              useAltTitle={vertical === 'today'}
              titleSize="small"
              titleBgColor="green"
              showStripeBackground={showStripeBackground}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export { RecirculationBacon };
